import {
  countPendingChunks,
  listRecordingChunks,
  listRecordingSessions,
  removeRecordingChunk,
  updateRecordingSession
} from './recording-queue.js';
import {
  finishRecordingSession,
  startRecordingSession,
  uploadRecordingChunk
} from './recording-api.js';
import { sendRecordingToMyCloud } from '$lib/streamer-client.js';

let activeSync = null;
let syncRequested = false;
const startedRecordingSessions = new Set();
const finalizedRecordingSessions = new Set();

function isNetworkError(error) {
  const message = String(error?.message || error || '').toLowerCase();
  return error instanceof TypeError
    || message.includes('failed to fetch')
    || message.includes('network')
    || message.includes('load failed')
    || message.includes('connection');
}

async function syncSession(session, onProgress) {
  if (session.state === 'recording' && !startedRecordingSessions.has(session.sessionId)) {
    await startRecordingSession({
      sessionId: session.sessionId,
      mimeType: session.mimeType,
      preferredMimeType: session.preferredMimeType,
      segmentDuration: session.segmentDuration,
      startedAt: session.startedAt,
      capture: session.capture || null,
      outputFormat: session.outputFormat || 'mp4',
      fileNamePattern: session.fileNamePattern || null,
      shareToCloud: session.shareToCloud !== false,
      ffmpeg: session.ffmpeg || null,
      videoBitsPerSecond: session.videoBitsPerSecond || null,
      audioBitsPerSecond: session.audioBitsPerSecond || null
    });

    startedRecordingSessions.add(session.sessionId);
    finalizedRecordingSessions.delete(session.sessionId);
  }

  const chunks = await listRecordingChunks(session.sessionId);

  for (const chunk of chunks) {
    await uploadRecordingChunk(chunk);
    await removeRecordingChunk(chunk.id);
    onProgress?.({
      type: 'chunk-uploaded',
      sessionId: session.sessionId,
      index: chunk.index
    });
  }

  if (session.state === 'stopping') {
    const remaining = await listRecordingChunks(session.sessionId);

    if (remaining.length === 0 && !finalizedRecordingSessions.has(session.sessionId)) {
      const result = await finishRecordingSession({
        sessionId: session.sessionId,
        expectedChunks: session.expectedChunks,
        stoppedAt: session.stoppedAt
      });
      finalizedRecordingSessions.add(session.sessionId);
      startedRecordingSessions.delete(session.sessionId);

      let cloudSharedAt = session.cloudSharedAt || null;

      if (result.state === 'completed' && session.shareToCloud !== false && !cloudSharedAt) {
        const shared = sendRecordingToMyCloud(result);

        if (!shared) {
          throw new Error('My Cloud chưa sẵn sàng, sẽ gửi video lại');
        }

        cloudSharedAt = new Date().toISOString();
      }

      await updateRecordingSession(session.sessionId, {
        state: result.state === 'completed' ? 'completed' : 'stopping',
        outputUrl: result.outputUrl || null,
        cloudSharedAt,
        syncError: null
      });

      onProgress?.({
        type: result.state === 'completed' ? 'completed' : 'waiting',
        sessionId: session.sessionId,
        result
      });
    }
  }

  if (session.state === 'completed') {
    startedRecordingSessions.delete(session.sessionId);
    finalizedRecordingSessions.delete(session.sessionId);
  }
}

export async function syncPendingRecordings({ onProgress } = {}) {
  if (activeSync) {
    syncRequested = true;
    return activeSync;
  }

  activeSync = (async () => {
    let networkUnavailable = false;

    do {
      syncRequested = false;

      const sessions = (await listRecordingSessions())
        .filter((session) => session.state !== 'completed')
        .sort((a, b) => String(a.startedAt).localeCompare(String(b.startedAt)));

      for (const session of sessions) {
        try {
          await syncSession(session, onProgress);
        } catch (error) {
          await updateRecordingSession(session.sessionId, {
            syncError: error.message
          });

          onProgress?.({
            type: 'sync-error',
            sessionId: session.sessionId,
            error
          });

          if (!navigator.onLine || isNetworkError(error)) {
            networkUnavailable = true;
            syncRequested = false;
            break;
          }
        }
      }
    } while (syncRequested && navigator.onLine && !networkUnavailable);

    return countPendingChunks();
  })().finally(() => {
    activeSync = null;
  });

  return activeSync;
}

export function enableRecordingRecovery(onProgress) {
  const handleOnline = () => {
    syncPendingRecordings({ onProgress }).catch(() => {});
  };

  window.addEventListener('online', handleOnline);

  return () => window.removeEventListener('online', handleOnline);
}
